import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { X, Plus } from 'lucide-react';
import debounce from 'lodash.debounce';
import api from 'config/axiosConfig';
import { parseWikipediaData } from 'util/wikiUtils';
import { Issue, IssueSuggestion, WikiSuggestion } from 'util/constants';
import InputField from 'ui/InputField';
import EntityAvatar from 'components/Entities/EntityAvatar';

interface Props {
  onSelect: (suggestion: Suggestion) => void;
  onEntityChipClose?: () => void;
  onSearchChange?: () => void;
  placeholder?: string;
  excludeIssueIds?: number[] | any;
  inputType?: 'search' | 'add';
  className?: string;
  autoFocus?: boolean;
  fieldRequired?: boolean;
  validationMessage?: string;
  validationState?: 'none' | 'valid' | 'invalid';
  fieldValue?: string;
  excludeCompoundIssues?: boolean;
  prepopulateData?: Issue | Suggestion | null;
}


export type Suggestion =
  | {
      type: 'issue';
      id: number;
      name: string;
      cname: string;
      wikipedia_link: string;
      subject_properties?: any;
    }
  | {
      type: 'wiki';
      id?: number;
      name: string;
      link: string;
      thumbnail: string;
      cname: string;
      subject_properties?: any;
    };


const IssueSearch: React.FC<Props> = ({ 
  onSelect, 
  onSearchChange,
  placeholder = "Search issues...",
  excludeIssueIds = [],
  inputType = 'search',
  className = "",
  autoFocus = false,
  fieldRequired = false,
  fieldValue = '',
  validationMessage = '',
  validationState = 'none',
  excludeCompoundIssues = false,
  onEntityChipClose,
  prepopulateData = null
}) => {
  const [searchQuery, setSearchQuery] = useState(fieldValue || '');
  const [searchSuggestions, setSearchSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [issueSelected, setIssueSelected] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;
  
  useLayoutEffect(() => {
    if (prepopulateData) {
      setSearchQuery(prepopulateData.cname);
      setIssueSelected(true);
      setSelectedSuggestion({
        type: 'issue' as const,
        ...prepopulateData,
        cname: prepopulateData.cname
      });
    } else {
      setIssueSelected(false);
      setSelectedSuggestion(null);
      setSearchQuery('');
    }
  }, [prepopulateData]);

  useEffect(() => {
      if (showSuggestions) {
          document.body.style.overflow = 'hidden';
      } else {
          document.body.style.overflow = '';
      }

      return () => {
          document.body.style.overflow = '';
      };
  }, [showSuggestions]);

  const performSearch = async (value: string) => {
    if (!value) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      // 1. Fetch issue suggestions from our backend
      const response = await api.get(`/api/issues?search=${encodeURIComponent(value)}&excludeIssueIds=[${excludeIssueIds.join(',')}]&excludeCompoundIssues=${excludeCompoundIssues}`);
      const issueResults: Issue[] = response.data.filter((issue: Issue) => 
        !excludeIssueIds.some((excludeIssueId:number) => excludeIssueId === issue.id)
      );

      const issueSuggestions: IssueSuggestion[] = issueResults.map((issue) => ({
        ...issue,
        type: 'issue' as const
      }));

      // 2. Calculate how many more results we need
      const remainingResults = 15 - issueSuggestions.length;

      // 3. If we still have room, fetch from Wikipedia
      let wikiSuggestions: Suggestion[] = [];
      if (remainingResults > 0) {
        const wikiResponse = await fetch(
          `https://en.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(
            value
          )}&limit=${remainingResults}&namespace=0&format=json&origin=*`
        );
        const data = await wikiResponse.json();

        const existingCnames = new Set([
          ...issueResults.map(issue => issue.cname.toLowerCase())
        ]);

        const allWiki = await Promise.all(
          data[1].map(async (title: string) => {
            try {
              const summaryResponse = await fetch(
                `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`
              );
              const summaryData = await summaryResponse.json();
              const canonicalTitle = summaryData.titles.canonical;

              if (existingCnames.has(canonicalTitle.toLowerCase()) || summaryData.type !== 'standard') {
                return null;
              }

              try {
                const existingIssueResponse = await api.get(`/api/issues/name/${encodeURIComponent(canonicalTitle)}`);
                
                if (existingIssueResponse.status === 200 && 
                    !excludeIssueIds.some((excludeIssueId:number) => excludeIssueId === existingIssueResponse.data.id)) {
                  return {
                    type: 'issue' as const,
                    ...existingIssueResponse.data,
                    cname: canonicalTitle
                  };
                }
              } catch (error) {
                const subjectParseProperties = await parseWikipediaData(canonicalTitle, false);
                return {
                  type: 'wiki' as const,
                  name: summaryData.title,
                  link: summaryData.content_urls.desktop.page,
                  thumbnail: summaryData.thumbnail?.source,
                  cname: canonicalTitle,
                  subject_properties: {
                    ...summaryData,
                    ...subjectParseProperties,
                  },
                  short_description: subjectParseProperties.wiki_parse_wikibase_shortdesc,
                };
              }
              return null;
            } catch (error) {
              console.error('Error fetching page summary:', error);
              return null;
            }
          })
        );

        wikiSuggestions = allWiki.filter((item): item is Suggestion => item !== null);
      }

      const combinedSuggestions = [...issueSuggestions, ...wikiSuggestions];
      setSearchSuggestions(combinedSuggestions as Suggestion[]);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => performSearch(value), 200),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);
    debouncedSearch(value);
    if (onSearchChange) {
      onSearchChange();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    searchInputRef.current?.blur();
  };

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    onSelect(suggestion);
    setSelectedSuggestion(suggestion);
    setShowSuggestions(false);
    setSearchQuery(suggestion.name);
    setIssueSelected(true);
  };

  const handleEntityChipClose = () => {
    setSelectedSuggestion(null);
    setIssueSelected(false);
    setSearchQuery('');
    fieldValue = '';
    if (onEntityChipClose) {
      onEntityChipClose();
    }
    document.getElementById(`issue-add-${placeholder}`)?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
      {inputType === 'search' ? (
        <>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            ref={searchInputRef}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-2 py-2 pr-8"
          />
          {searchQuery.length > 0 && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </>
        ) : (
        <>
          {issueSelected ? (
            <span className={`input-container relative flex-1`}>
                <div
                  className={`
                      w-full min-h-[60px] px-3 pt-6 pb-1
                      eoe-input bg-gray-200 dark:bg-eoe-dark-input
                      rounded-2xl
                      text-gray-800 dark:text-eoe-dark-text
                      transition-colors
                      resize-none
                  `}
                >
                <span 
                    className={`
                        absolute left-3
                        top-2 text-xs
                        transition-all
                        text-eoe-text-meta dark:text-eoe-dark-text-meta
                    `}
                >
                    {placeholder}
                    {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                </span>
                <EntityAvatar
                  entity={selectedSuggestion as unknown as Issue}
                  entityType="issue"
                  entityChip={true}
                  showCloseChip={true}
                  onEntityChipClose={handleEntityChipClose}
                />
                </div>
              </span>
          ) : (
            <InputField
              id={`issue-add-${placeholder}`}
              type="text"
              name={`issue-add-${placeholder}`}
              labelText={placeholder}
              fieldRequired={fieldRequired}
              value={searchQuery || fieldValue}
              onChange={handleSearchChange}
              autoComplete="off"
              ref={searchInputRef}
              autoFocus={prepopulateData ? true : autoFocus}
              validationState={validationState}
              error={validationMessage}
            />
          )}
        </>
      )}
      </div>

      {showSuggestions && searchSuggestions.length > 0 && (
      <div
        className={`search-suggestions-container flex flex-col bg-transparent 
          ${inputType === 'search' ?
          'fixed top-[56px] left-[1px] right-[1px] z-50 max-h-[calc(100dvh-72px)] overflow-hidden md:w-[580px] md:mx-auto md:left-0 md:right-0' : 
          `relative bottom-2 top-1 max-h-[200px] overflow-y-auto`}
        `}
      >
        <ul
          className={`bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 shadow-lg
            ${inputType === 'search' ? 
              'flex-shrink overflow-y-auto max-h-[calc(100dvh-72px)]' :
              'flex-1 overflow-y-auto'
            }
          `}
        >
          {searchSuggestions.map((suggestion, index) => (
            <li
              key={index}
              className="
                group
                px-3 py-2
                hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon
                cursor-pointer
                text-eoe-text dark:text-eoe-dark-text
                flex justify-between items-center
              "
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              <div className="flex items-center space-x-2">
                <span className="w-8 h-8">
                  <img src={(suggestion.type === 'wiki' && (suggestion as WikiSuggestion).thumbnail && (suggestion as WikiSuggestion).thumbnail.trim() !== '' ? (suggestion as WikiSuggestion).thumbnail : 
                  (suggestion as IssueSuggestion).properties?.reference_link_img_key ? `${IMAGE_CDN}/${(suggestion as IssueSuggestion).properties.reference_link_img_key}` : (suggestion as IssueSuggestion).properties?.subject_properties?.thumbnail?.source ? `${(suggestion as IssueSuggestion).properties.subject_properties.thumbnail.source}` : 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg')} alt="Issue avatar" className="w-full h-full rounded-full object-cover" />
                </span>
                <div className="flex flex-col">
                  <span className={suggestion.type === 'wiki' ? 'italic' : ''}>
                    {suggestion.name}
                  </span>
                  <span className={`text-xs ${suggestion.type === 'wiki' ? 'italic' : ''}`}>
                      {(suggestion as WikiSuggestion).short_description || (suggestion as IssueSuggestion).properties?.subject_properties?.wiki_parse_wikibase_shortdesc as string}
                  </span>
                </div>
              </div>
              {suggestion.type === 'wiki' ? (
                <div className="flex items-center space-x-0 p-1 rounded-full group-hover:bg-eoe-icon-hover dark:group-hover:bg-eoe-dark-icon-hover text-eoe-gray dark:text-eoe-dark-text flex-shrink-0"><img src='https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg' alt='Wikipedia Logo' className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text flex-shrink-0" /><Plus className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text flex-shrink-0" /></div>
              ) : (<span className="flex rounded-full px-2 text-xs bg-eoe-entity-issue text-white flex-shrink-0 items-center justify-center">Issue</span>)}
            </li>
          ))}
        </ul>
        <div className="flex-shrink-0 border-none"> 
          <button className="w-full btn-secondary py-[5px] px-2 rounded-tr-none rounded-tl-none" onClick={() => setShowSuggestions(false)}>
            <span className="text-eoe-dark-text">Close search</span>
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default IssueSearch;